import { OrderType, useConfig, useSession } from '@travelwin/core';
import { useEffect } from 'react';

import { getAdvertiserIdFromVoucher } from '../../services/order/VoucherService';
import { getCampaignParametersFromStorage } from '../../utils/campaignUtils';

interface AwinTracking {
  Sale?: {
    amount: string;
    orderRef: string;
    parts: string;
    voucher: string;
    currency: string;
    test: string;
    channel: string;
  };
  run?: () => void;
}

declare global {
  interface Window {
    AWIN?: {
      Tracking?: AwinTracking;
    };
  }
}

const resolveAwinAdvertiserId = ({
  awinAdvertiserIdFromVoucher,
  awc,
  awinAdvertiserIdsString,
}: {
  awinAdvertiserIdFromVoucher?: string | null;
  awc?: string | null;
  awinAdvertiserIdsString?: string;
}) => {
  const awinAdvertiserIds = awinAdvertiserIdsString
    ?.split(',')
    ?.flatMap((ids: string) => ids.split(';')[0]);

  let candidate = awinAdvertiserIds?.[0] || '';

  if (awinAdvertiserIdFromVoucher) {
    candidate = awinAdvertiserIdFromVoucher;
  } else if (awc) {
    const awcParts = awc?.split('_');
    if (/^[0-9]{5}$/.test(awcParts[0])) {
      candidate = awcParts[0];
    }
  }
  return awinAdvertiserIds?.includes(candidate.toString()) ? candidate : null;
};

/**
 * Implementation according to:
 * https://developer.awin.com/docs/standard-implementation
 * https://wiki.awin.com/index.php/Advertiser_Tracking_Guide
 */
export function AwinMasterTag() {
  const campaignParams = getCampaignParametersFromStorage();

  const { AWIN_ADVERTISER_IDS } = useConfig();
  const awinAdvertiserId = resolveAwinAdvertiserId({
    awc: campaignParams?.awin_awc,
    awinAdvertiserIdsString: AWIN_ADVERTISER_IDS,
  });

  useEffect(() => {
    let awinMasterTagScript: HTMLScriptElement;

    if (awinAdvertiserId) {
      awinMasterTagScript = document.createElement('script');
      awinMasterTagScript.src = `https://www.dwin1.com/${awinAdvertiserId}.js`;
      awinMasterTagScript.setAttribute('type', 'text/javascript');
      awinMasterTagScript.defer = true;
      document.body.appendChild(awinMasterTagScript);
    }

    return () => {
      if (awinMasterTagScript) {
        awinMasterTagScript.remove();
      }
    };
  }, [awinAdvertiserId]);

  return null;
}

export const useAwinTransactionConfirmation = () => {
  const {
    AWIN_ADVERTISER_IDS,
    AWIN_SALES_CHANNEL,
    AWIN_TEST,
    AWIN_PIXEL_TRACKING_ENABLED,
  } = useConfig();

  const { sessionId, origin } = useSession();

  return async (order: OrderType) => {
    const awc = order.campaign_parameters?.awin_awc;
    const voucherCode =
      order.payments.find((p) => p.payment_type.id === 'VOUCHER')
        ?.payment_reference || '';

    let awinAdvertiserIdFromVoucher: string | null = null;
    if (voucherCode) {
      try {
        awinAdvertiserIdFromVoucher = await getAdvertiserIdFromVoucher({
          sessionId,
          voucherCode,
          origin,
        });
      } catch {
        // voucher not found, skip it
      }
    }

    const awinAdvertiserId = resolveAwinAdvertiserId({
      awinAdvertiserIdFromVoucher,
      awc,
      awinAdvertiserIdsString: AWIN_ADVERTISER_IDS,
    });

    if (!awinAdvertiserId || (!awc && !voucherCode)) {
      return;
    }

    const orderTotal = order.total_price.toFixed(2);
    const currencyCode = order.currency?.alias?.toUpperCase();
    const orderNo = order.order_number;
    const awinTest = AWIN_TEST ? '1' : '0';

    if (window.AWIN?.Tracking) {
      window.AWIN.Tracking.Sale = {
        amount: `${orderTotal}`,
        orderRef: orderNo,
        parts: `DEFAULT:${orderTotal}`,
        voucher: voucherCode,
        currency: currencyCode,
        test: awinTest,
        channel: AWIN_SALES_CHANNEL,
      };

      /*
        We got reports, that pixel tracking causes duplication with s2s tracking.
        Hence, we disable it with a feature flag. If after some time it proves to be correct, consider removing completely.
       */
      if (AWIN_PIXEL_TRACKING_ENABLED) {
        const awinPixel = new Image(0, 0);
        awinPixel.src = `https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=${awinAdvertiserId}&amount=${orderTotal}&cr=${currencyCode}&ref=${orderNo}&parts=DEFAULT:${orderTotal}&vc=${voucherCode}&ch=${AWIN_SALES_CHANNEL}&testmode=${awinTest}`;
      }

      window.AWIN.Tracking.run?.();
    }
  };
};
